import React, { useState, useEffect } from 'react';
import { CheckCircle, XCircle, Activity, Clock, AlertTriangle, EyeOff, RotateCcw } from 'lucide-react';
import { useRafraichissementAuto } from '../rafraichissementAuto';

/* ─────────────── parcours des visiteurs et santé technique ───────────────
   Deux questions auxquelles cet encart répond d'un coup d'œil :
   « est-ce que quelque chose est cassé ? » et « qui s'est arrêté en route ? ».
   Les incidents sont écrits en clair, avec le message exact de Monetico.
   Le même encart est affiché dans Paiements en direct et dans Dossiers
   E-Mandat : c'est la même réalité, vue depuis deux écrans. */

const API = process.env.REACT_APP_API_URL || '/api';
const jetonEntete = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const dateHeure = (v) => (v ? new Date(String(v).replace(' ', 'T')).toLocaleString('fr-FR',
  { timeZone: 'Europe/Paris', day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit' }) : '—');

export const NIVEAU = {
  incident:  { fond: '#ffebee', bord: '#ffcdd2', couleur: '#b71c1c' },
  attention: { fond: '#fff8e1', bord: '#ffe0b2', couleur: '#8d6e00' },
  info:      { fond: '#f5f7fa', bord: '#e6eaf0', couleur: '#546e7a' }
};

export default function PanneauParcours({ titre }) {
  const [p, setP] = useState(null);
  const [erreur, setErreur] = useState(null);
  const [controleEnCours, setControle] = useState(false);
  const [journalOuvert, setJournal] = useState(false);
  const [masquesOuverts, setMasques] = useState(false);

  /* Retirer un dossier de la liste de relance, ou l'y remettre. Rien n'est
     supprime : le dossier reste entier, il cesse juste de demander une action. */
  const changerSuivi = async (c, masquer) => {
    try {
      await fetch(`${API}/emandat/clients/${c.id}/suivi`, {
        method: 'POST', headers: { ...jetonEntete(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ masquer })
      });
      await charger();
    } catch (e) { setErreur(e.message); }
  };

  const charger = async () => {
    try {
      const r = await fetch(`${API}/emandat/parcours`, { headers: jetonEntete() });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || `Erreur ${r.status}`);
      setP(j); setErreur(null);
    } catch (e) { setErreur(e.message); }
  };
  useEffect(() => { charger(); /* eslint-disable-next-line */ }, []);
  useRafraichissementAuto(charger, { secondes: 45 });

  const controler = async () => {
    setControle(true);
    try {
      await fetch(`${API}/emandat/controle-paiement`, { method: 'POST', headers: jetonEntete() });
      await charger();
    } catch (e) { setErreur(e.message); }
    setControle(false);
  };

  if (erreur) return (
    <div style={{ background: '#ffebee', color: '#b71c1c', padding: '12px 16px',
                  borderRadius: 8, marginBottom: 14, fontSize: 13 }}>
      Suivi technique ES-PROTECT indisponible : {erreur}
    </div>
  );
  if (!p) return null;

  const sain = p.paiement && p.paiement.sain;
  const enPlan = p.en_plan || [];
  const incidents = p.incidents || [];

  return (
    <div style={{ marginBottom: 18 }}>
      {titre && (
        <h3 style={{ margin: '0 0 9px', fontSize: 13, textTransform: 'uppercase',
                     letterSpacing: '.05em', color: '#667eea' }}>{titre}</h3>
      )}

      {/* état du paiement : c'est ce qui empêche ou non d'ouvrir un dossier */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 11, flexWrap: 'wrap',
                    background: sain ? '#f1f8f2' : '#ffebee',
                    border: `1px solid ${sain ? '#c8e6c9' : '#ffcdd2'}`,
                    borderRadius: 8, padding: '12px 15px' }}>
        {sain ? <CheckCircle size={18} color="#2e7d32" style={{ marginTop: 1 }} />
              : <XCircle size={18} color="#b71c1c" style={{ marginTop: 1 }} />}
        <div style={{ flex: 1, minWidth: 260 }}>
          <b style={{ fontSize: 13.5, color: sain ? '#1b5e20' : '#b71c1c' }}>
            {sain ? 'ES-PROTECT : les ouvertures de dossier fonctionnent'
                  : 'ES-PROTECT : personne ne peut valider sa carte en ce moment'}
          </b>
          <p style={{ margin: '3px 0 0', fontSize: 12.5, color: '#546e7a' }}>
            {sain
              ? <>La page de saisie de carte s’affiche normalement. Contrôle automatique toutes les
                  30 minutes, avec {p.paiement.montant} € sur le terminal {p.paiement.terminal}.</>
              : <>Monetico refuse le formulaire : <b>« {p.paiement.message} »</b>. Tant que ce message
                  persiste, un visiteur qui ouvre un dossier tombe sur une page d’erreur.</>}
            {p.paiement.verifie_le && <> Dernier contrôle à {dateHeure(p.paiement.verifie_le)}.</>}
          </p>
        </div>
        <button onClick={controler} disabled={controleEnCours}
                style={{ padding: '7px 12px', border: '1px solid #dfe3ea', borderRadius: 6,
                         background: '#fff', fontSize: 12.5, cursor: 'pointer',
                         display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}>
          <Activity size={14} /> {controleEnCours ? 'Contrôle…' : 'Vérifier maintenant'}
        </button>
      </div>

      {/* ceux qui ont commencé sans aller au bout */}
      {enPlan.length > 0 && (
        <div style={{ marginTop: 12, border: '1px solid #ffe0b2', background: '#fffaf2',
                      borderRadius: 8, padding: '12px 15px' }}>
          <b style={{ fontSize: 13.5, color: '#e65100', display: 'flex', alignItems: 'center', gap: 7 }}>
            <Clock size={16} /> {enPlan.length} dossier{enPlan.length > 1 ? 's' : ''} ouvert
            {enPlan.length > 1 ? 's' : ''} sans carte validée
          </b>
          <p style={{ margin: '3px 0 9px', fontSize: 12.5, color: '#8d6e00' }}>
            Voici précisément où chacun s’est arrêté.
          </p>
          {enPlan.map((c) => (
            <div key={c.id} style={{ display: 'flex', gap: 12, alignItems: 'flex-start',
                                     padding: '8px 0', borderTop: '1px solid #ffe9cc' }}>
              <div style={{ flex: 1, minWidth: 200 }}>
                <b style={{ fontSize: 13 }}>{c.prenom} {c.nom}</b>
                <span style={{ color: '#90a4ae', fontSize: 12.5 }}> · {c.email}</span>
                <p style={{ margin: '2px 0 0', fontSize: 12.5, color: '#546e7a' }}>
                  {c.dernier_detail || 'Aucun détail enregistré.'}
                </p>
              </div>
              <div style={{ textAlign: 'right', flex: 'none' }}>
                <span style={{ background: c.abandonne ? '#ffebee' : '#e8f1ff',
                               color: c.abandonne ? '#b71c1c' : '#14448f',
                               borderRadius: 999, padding: '3px 10px', fontSize: 12, fontWeight: 600 }}>
                  {c.libelle_etape}
                </span>
                <div style={{ fontSize: 11.5, color: '#90a4ae', marginTop: 3 }}>
                  {c.abandonne ? 'parti' : 'en cours'} · il y a {c.minutes_depuis} min
                </div>
                <button onClick={() => changerSuivi(c, true)}
                        title="Retirer de cette liste. Le dossier n’est pas supprimé et reste consultable."
                        style={{ marginTop: 6, border: '1px solid #e0e4ec', borderRadius: 6,
                                 background: '#fff', color: '#78909c', fontSize: 11.5,
                                 padding: '4px 9px', cursor: 'pointer', display: 'inline-flex',
                                 alignItems: 'center', gap: 5 }}>
                  <EyeOff size={12} /> Retirer
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {(p.masques || []).length > 0 && (
        <div style={{ marginTop: 8 }}>
          <button onClick={() => setMasques(!masquesOuverts)}
                  style={{ background: 'none', border: 'none', color: '#3949ab', fontSize: 12.5,
                           cursor: 'pointer', padding: 0 }}>
            {masquesOuverts ? 'Masquer' : 'Voir'} les {p.masques.length} dossier
            {p.masques.length > 1 ? 's' : ''} retiré{p.masques.length > 1 ? 's' : ''} de cette liste
          </button>
          {masquesOuverts && (
            <div style={{ marginTop: 8, border: '1px solid #eceff1', borderRadius: 8 }}>
              {p.masques.map((c) => (
                <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 10,
                                         padding: '8px 12px', borderBottom: '1px solid #f4f6f8',
                                         fontSize: 12.5, color: '#546e7a' }}>
                  <div style={{ flex: 1 }}>
                    <b>{c.prenom} {c.nom}</b> · {c.email}
                    <span style={{ color: '#b0bec5' }}> — retiré le {dateHeure(c.suivi_masque_le)}</span>
                  </div>
                  <button onClick={() => changerSuivi(c, false)}
                          style={{ border: '1px solid #e0e4ec', borderRadius: 6, background: '#fff',
                                   color: '#3949ab', fontSize: 11.5, padding: '4px 9px',
                                   cursor: 'pointer', display: 'inline-flex', alignItems: 'center',
                                   gap: 5 }}>
                    <RotateCcw size={12} /> Remettre
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* incidents techniques, message d'origine compris */}
      {incidents.length > 0 && (
        <div style={{ marginTop: 12, border: '1px solid #ffcdd2', background: '#fff5f5',
                      borderRadius: 8, padding: '12px 15px' }}>
          <b style={{ fontSize: 13.5, color: '#b71c1c', display: 'flex', alignItems: 'center', gap: 7 }}>
            <AlertTriangle size={16} /> {incidents.length} incident{incidents.length > 1 ? 's' : ''} technique
            {incidents.length > 1 ? 's' : ''}
          </b>
          {incidents.slice(0, 6).map((i) => (
            <div key={i.id} style={{ padding: '8px 0', borderTop: '1px solid #ffe3e3' }}>
              <b style={{ fontSize: 12.8 }}>{i.titre || i.etape}</b>
              <span style={{ color: '#90a4ae', fontSize: 11.5 }}> · {dateHeure(i.cree_le)}</span>
              <p style={{ margin: '2px 0 0', fontSize: 12.5, color: '#7a2f2f' }}>{i.detail}</p>
            </div>
          ))}
        </div>
      )}

      {/* journal complet, replié par défaut */}
      <div style={{ marginTop: 10 }}>
        <button onClick={() => setJournal(!journalOuvert)}
                style={{ background: 'none', border: 'none', color: '#3949ab', fontSize: 12.5,
                         cursor: 'pointer', padding: 0 }}>
          {journalOuvert ? 'Masquer' : 'Voir'} le journal détaillé ({(p.recents || []).length} dernières traces)
        </button>
        {journalOuvert && (
          <div style={{ marginTop: 8, maxHeight: 320, overflowY: 'auto', border: '1px solid #eceff1',
                        borderRadius: 8 }}>
            {(p.recents || []).map((e) => {
              const n = NIVEAU[e.niveau] || NIVEAU.info;
              return (
                <div key={e.id} style={{ display: 'flex', gap: 10, padding: '7px 12px',
                                         borderBottom: '1px solid #f4f6f8', fontSize: 12.5 }}>
                  <span style={{ color: '#b0bec5', flex: 'none', width: 118 }}>{dateHeure(e.cree_le)}</span>
                  <span style={{ background: n.fond, color: n.couleur, border: `1px solid ${n.bord}`,
                                 borderRadius: 999, padding: '1px 9px', flex: 'none', fontSize: 11.5,
                                 fontWeight: 600, height: 'fit-content' }}>
                    {(p.etapes && p.etapes[e.etape]) || e.etape}
                  </span>
                  <span style={{ color: '#546e7a' }}>
                    {e.prenom ? <b>{e.prenom} {e.nom} · </b> : null}{e.detail || e.titre || ''}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
